import React, { useCallback, useEffect, useState } from 'react';
import {
  Check, X, Clock, CheckCircle, XCircle, RefreshCw, FileText, Gift, Tag, Plus, Pencil, Trash2, Save,
} from 'lucide-react';
import api from '../api/client';

// ── Turlar ───────────────────────────────────────────────────────────────────

type Unit = 'day' | 'month' | 'year';
type Status = 'pending' | 'approved' | 'rejected';

interface Plan {
  id: number;
  name: string;
  price: string;
  duration_value: number;
  duration_unit: Unit;
  duration_label: string;
  is_active: boolean;
  order: number;
}

interface Payment {
  id: number;
  shop: number;
  shop_name: string;
  owner_name: string;
  owner_phone: string;
  kind: 'payment' | 'free';
  plan_name: string;
  duration_label: string;
  expected_amount: string;
  amount: string;
  has_receipt: boolean;
  note: string;
  status: Status;
  rejection_reason: string;
  period_end: string | null;
  created_at: string;
}

interface BillingShop {
  id: number;
  name: string;
  owner_name: string;
  owner_phone: string;
  is_active: boolean;
  subscription_until: string;
  active: boolean;
  days_left: number;
  custom_prices: Record<string, string>;
}

interface BillingSettings {
  trial_days: number;
  card_number: string;
  card_holder: string;
  payment_note: string;
}

const UNITS: { value: Unit; label: string }[] = [
  { value: 'day', label: 'Kun' },
  { value: 'month', label: 'Oy' },
  { value: 'year', label: 'Yil' },
];

const STATUS_META: Record<Status, { label: string; cls: string; Icon: React.FC<any> }> = {
  pending: { label: "Ko'rib chiqilmoqda", cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  approved: { label: 'Tasdiqlangan', cls: 'bg-success-100 text-success-700', Icon: CheckCircle },
  rejected: { label: 'Rad etilgan', cls: 'bg-red-100 text-red-700', Icon: XCircle },
};

const money = (v: string | number) => Number(v).toLocaleString('ru-RU').replace(/,/g, ' ');
const day = (iso: string) => new Date(iso).toLocaleDateString('ru-RU');
const errText = (e: any, fallback: string) => {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d.error === 'string') return d.error;
  const first = Object.values(d)[0];
  return Array.isArray(first) ? String(first[0]) : fallback;
};

const Dialog: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
    <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      {children}
    </div>
  </div>
);

// ── Sahifa ───────────────────────────────────────────────────────────────────

type Tab = 'payments' | 'shops' | 'plans' | 'settings';
const TABS: { key: Tab; label: string }[] = [
  { key: 'payments', label: 'Cheklar' },
  { key: 'shops', label: "Do'konlar" },
  { key: 'plans', label: 'Tariflar' },
  { key: 'settings', label: 'Sozlamalar' },
];

export const Subscriptions: React.FC = () => {
  const [tab, setTab] = useState<Tab>('payments');
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Obunalar</h1>
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              tab === t.key ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'shops' && <ShopsTab />}
      {tab === 'plans' && <PlansTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
};

// ── Cheklar ──────────────────────────────────────────────────────────────────

const PaymentsTab: React.FC = () => {
  const [items, setItems] = useState<Payment[]>([]);
  const [filter, setFilter] = useState<Status | ''>('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<Payment | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/billing/payments/', { params: filter ? { status: filter } : {} });
      setItems(res.data.results ?? res.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openReceipt = async (p: Payment) => {
    const res = await api.get(`/billing/payments/${p.id}/receipt/`, { responseType: 'blob' });
    window.open(URL.createObjectURL(res.data), '_blank');
  };

  const approve = async (p: Payment) => {
    if (!window.confirm(`${p.shop_name}: ${money(p.amount)} so'm to'lovni tasdiqlaysizmi? Obuna ${p.duration_label}ga uzaytiriladi.`)) return;
    setBusyId(p.id);
    try {
      await api.post(`/billing/payments/${p.id}/approve/`);
      await load();
    } catch (e) {
      alert(errText(e, 'Xato'));
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    if (!reason.trim()) return setError('Sababini yozing');
    setBusyId(rejecting.id);
    try {
      await api.post(`/billing/payments/${rejecting.id}/reject/`, { reason });
      setRejecting(null);
      setReason('');
      await load();
    } catch (e) {
      setError(errText(e, 'Xato'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(['pending', 'approved', 'rejected', ''] as const).map((s) => (
          <button key={s || 'all'} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm ${filter === s ? 'bg-slate-900 text-white' : 'bg-white text-gray-600'}`}>
            {s ? STATUS_META[s].label : 'Hammasi'}
          </button>
        ))}
        <button onClick={load} className="ml-auto btn btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Yangilash
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Yuklanmoqda...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Chek yo'q</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => {
            const meta = STATUS_META[p.status];
            const free = p.kind === 'free';
            const short = !free && Number(p.amount) < Number(p.expected_amount);
            return (
              <div key={p.id} className="card space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">{p.shop_name}</div>
                    <div className="text-xs text-gray-500">{p.owner_name} · {p.owner_phone}</div>
                  </div>
                  {free ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-800">
                      <Gift className="w-3 h-3" /> Bepul
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-700">
                  <div>{p.plan_name} · {p.duration_label}</div>
                  {!free && (
                    <div>
                      To'langan: <b>{money(p.amount)}</b> so'm
                      <span className={short ? 'text-red-600' : 'text-gray-400'}> / kerak {money(p.expected_amount)}</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">{day(p.created_at)}</div>
                  {p.note && <div className="text-xs text-gray-500 mt-1">Izoh: {p.note}</div>}
                  {p.rejection_reason && <div className="text-xs text-red-600 mt-1">Sabab: {p.rejection_reason}</div>}
                  {p.period_end && p.status === 'approved' && (
                    <div className="text-xs text-success-700 mt-1">{day(p.period_end)} gacha uzaytirildi</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {p.has_receipt && (
                    <button onClick={() => openReceipt(p)} className="btn btn-secondary flex items-center gap-1.5">
                      <FileText className="w-4 h-4" /> Chek
                    </button>
                  )}
                  {p.status === 'pending' && (
                    <>
                      <button disabled={busyId === p.id} onClick={() => approve(p)}
                        className="btn btn-primary flex-1 flex items-center justify-center gap-1.5">
                        <Check className="w-4 h-4" /> Tasdiqlash
                      </button>
                      <button disabled={busyId === p.id} onClick={() => { setRejecting(p); setError(''); }}
                        className="btn btn-danger flex items-center justify-center gap-1.5">
                        <X className="w-4 h-4" /> Rad
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rejecting && (
        <Dialog title={`Rad etish — ${rejecting.shop_name}`} onClose={() => setRejecting(null)}>
          <label className="label">Sabab (tadbirkorga ko'rinadi)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="input"
            placeholder="Masalan: to'lov kartaga tushmagan / summa to'liq emas" />
          {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
          <div className="flex gap-2 mt-4">
            <button onClick={() => setRejecting(null)} className="btn btn-secondary flex-1">Bekor</button>
            <button onClick={submitReject} disabled={busyId === rejecting.id} className="btn btn-danger flex-1">Rad etish</button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

// ── Do'konlar ────────────────────────────────────────────────────────────────

const ShopsTab: React.FC = () => {
  const [shops, setShops] = useState<BillingShop[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState<BillingShop | null>(null);
  const [pricing, setPricing] = useState<BillingShop | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get('/billing/shops/', { params: search ? { search } : {} }),
        api.get('/billing/plans/'),
      ]);
      setShops(s.data.results ?? s.data);
      setPlans(p.data.results ?? p.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const replace = (shop: BillingShop) => setShops((list) => list.map((s) => (s.id === shop.id ? shop : s)));

  return (
    <div>
      <input className="input mb-4 max-w-sm" placeholder="Do'kon, egasi yoki telefon..." value={search}
        onChange={(e) => setSearch(e.target.value)} />
      {loading ? (
        <div className="text-center py-12 text-gray-500">Yuklanmoqda...</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shops.map((shop) => {
            const customCount = Object.keys(shop.custom_prices).length;
            return (
              <div key={shop.id} className="card space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">{shop.name}</div>
                    <div className="text-xs text-gray-500">{shop.owner_name} · {shop.owner_phone}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${
                    shop.active ? 'bg-success-100 text-success-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {shop.active ? `${shop.days_left} kun` : 'Tugagan'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Obuna: {day(shop.subscription_until)} gacha
                  {customCount > 0 && <div className="text-xs text-primary-800 mt-1">Maxsus narx: {customCount} ta tarif</div>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setGranting(shop)} className="btn btn-secondary flex-1 flex items-center justify-center gap-1.5 text-sm whitespace-nowrap px-2">
                    <Gift className="w-4 h-4" /> Bepul muddat
                  </button>
                  <button onClick={() => setPricing(shop)} className="btn btn-secondary flex-1 flex items-center justify-center gap-1.5 text-sm whitespace-nowrap px-2">
                    <Tag className="w-4 h-4" /> Narxlar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {granting && <GrantDialog shop={granting} onClose={() => setGranting(null)} onDone={(s) => { replace(s); setGranting(null); }} />}
      {pricing && <PricesDialog shop={pricing} plans={plans} onClose={() => setPricing(null)} onDone={(s) => { replace(s); setPricing(null); }} />}
    </div>
  );
};

const GrantDialog: React.FC<{ shop: BillingShop; onClose: () => void; onDone: (s: BillingShop) => void }> = ({ shop, onClose, onDone }) => {
  const [value, setValue] = useState('10');
  const [unit, setUnit] = useState<Unit>('day');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/billing/shops/${shop.id}/grant/`, { duration_value: Number(value), duration_unit: unit, note });
      onDone(res.data);
    } catch (e) {
      setError(errText(e, 'Xato'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Bepul muddat — ${shop.name}`} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">
        Joriy obuna tugash sanasiga qo'shiladi (tugagan bo'lsa — bugundan). Darhol amal qiladi.
      </p>
      <div className="flex gap-2">
        <input type="number" min={1} value={value} onChange={(e) => setValue(e.target.value)} className="input w-28" />
        <select value={unit} onChange={(e) => setUnit(e.target.value as Unit)} className="input flex-1">
          {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
        </select>
      </div>
      <label className="label mt-4">Izoh (tadbirkorga ko'rinadi)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} className="input" placeholder="Masalan: aksiya" />
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      <div className="flex gap-2 mt-4">
        <button onClick={onClose} className="btn btn-secondary flex-1">Bekor</button>
        <button onClick={submit} disabled={busy || !(Number(value) > 0)} className="btn btn-primary flex-1">Berish</button>
      </div>
    </Dialog>
  );
};

const PricesDialog: React.FC<{ shop: BillingShop; plans: Plan[]; onClose: () => void; onDone: (s: BillingShop) => void }> = ({ shop, plans, onClose, onDone }) => {
  const [values, setValues] = useState<Record<number, string>>(() =>
    Object.fromEntries(plans.map((p) => [p.id, shop.custom_prices[String(p.id)] ? String(Math.round(Number(shop.custom_prices[String(p.id)]))) : ''])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const body = plans.map((p) => ({ plan: p.id, price: values[p.id] === '' ? null : values[p.id] }));
      const res = await api.put(`/billing/shops/${shop.id}/prices/`, body);
      onDone(res.data);
    } catch (e) {
      setError(errText(e, 'Xato'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Maxsus narxlar — ${shop.name}`} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">Bo'sh qoldirilsa umumiy narx amal qiladi.</p>
      {plans.length === 0 && <div className="text-sm text-gray-500">Avval Tariflar bo'limida tarif yarating.</div>}
      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.id}>
            <label className="label">{p.name} · {p.duration_label} <span className="text-gray-400 font-normal">(umumiy {money(p.price)})</span></label>
            <input inputMode="numeric" value={values[p.id] ?? ''} placeholder={money(p.price)} className="input"
              onChange={(e) => setValues((v) => ({ ...v, [p.id]: e.target.value.replace(/[^\d]/g, '') }))} />
          </div>
        ))}
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      <div className="flex gap-2 mt-4">
        <button onClick={onClose} className="btn btn-secondary flex-1">Bekor</button>
        <button onClick={submit} disabled={busy || plans.length === 0} className="btn btn-primary flex-1">Saqlash</button>
      </div>
    </Dialog>
  );
};

// ── Tariflar ─────────────────────────────────────────────────────────────────

const EMPTY_PLAN = { name: '', price: '', duration_value: '1', duration_unit: 'month' as Unit, is_active: true, order: '0' };

const PlansTab: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [editing, setEditing] = useState<(typeof EMPTY_PLAN & { id?: number }) | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    const res = await api.get('/billing/plans/');
    setPlans(res.data.results ?? res.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setError('');
    const body = { ...editing, duration_value: Number(editing.duration_value), order: Number(editing.order) };
    try {
      if (editing.id) await api.put(`/billing/plans/${editing.id}/`, body);
      else await api.post('/billing/plans/', body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(errText(e, 'Xato'));
    }
  };

  const remove = async (p: Plan) => {
    if (!window.confirm(`"${p.name}" tarifini o'chirasizmi? Eski to'lovlar tarixda qoladi. Vaqtincha yashirish uchun "Faol" ni o'chiring.`)) return;
    await api.delete(`/billing/plans/${p.id}/`);
    await load();
  };

  return (
    <div>
      <button onClick={() => { setEditing({ ...EMPTY_PLAN }); setError(''); }} className="btn btn-primary flex items-center gap-2 mb-4">
        <Plus className="w-4 h-4" /> Yangi tarif
      </button>
      {plans.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Tarif yo'q. Tadbirkorlar to'lov qila olmaydi.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {plans.map((p) => (
            <div key={p.id} className={`card ${p.is_active ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-gray-900">{p.name}</div>
                  <div className="text-sm text-gray-500">{p.duration_label}</div>
                </div>
                {!p.is_active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Yashirin</span>}
              </div>
              <div className="text-xl font-bold mt-2">{money(p.price)} so'm</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setEditing({
                  id: p.id, name: p.name, price: String(Math.round(Number(p.price))), duration_value: String(p.duration_value),
                  duration_unit: p.duration_unit, is_active: p.is_active, order: String(p.order),
                }); setError(''); }} className="btn btn-secondary flex-1 flex items-center justify-center gap-1.5">
                  <Pencil className="w-4 h-4" /> Tahrirlash
                </button>
                <button onClick={() => remove(p)} className="btn btn-secondary text-red-600" aria-label="O'chirish">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Dialog title={editing.id ? 'Tarifni tahrirlash' : 'Yangi tarif'} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <div>
              <label className="label">Nomi</label>
              <input className="input" value={editing.name} placeholder="Masalan: Oylik"
                onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Narxi (so'm)</label>
              <input className="input" inputMode="numeric" value={editing.price}
                onChange={(e) => setEditing({ ...editing, price: e.target.value.replace(/[^\d]/g, '') })} />
            </div>
            <div>
              <label className="label">Muddati</label>
              <div className="flex gap-2">
                <input type="number" min={1} className="input w-28" value={editing.duration_value}
                  onChange={(e) => setEditing({ ...editing, duration_value: e.target.value })} />
                <select className="input flex-1" value={editing.duration_unit}
                  onChange={(e) => setEditing({ ...editing, duration_unit: e.target.value as Unit })}>
                  {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Faol (tadbirkorga ko'rinadi)
              </label>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500">Tartib</span>
                <input type="number" className="input w-20" value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
            </div>
          </div>
          {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
          <div className="flex gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="btn btn-secondary flex-1">Bekor</button>
            <button onClick={save} className="btn btn-primary flex-1">Saqlash</button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

// ── Sozlamalar ───────────────────────────────────────────────────────────────

const SettingsTab: React.FC = () => {
  const [data, setData] = useState<BillingSettings | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/billing/settings/').then((res) => setData(res.data)); }, []);

  if (!data) return <div className="text-center py-12 text-gray-500">Yuklanmoqda...</div>;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const res = await api.patch('/billing/settings/', data);
      setData(res.data);
      setMsg('Saqlandi ✓');
    } catch (err) {
      setMsg(errText(err, 'Saqlashda xato'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="card space-y-5 max-w-2xl">
      <div>
        <label className="label">Yangi do'konga bepul sinov (kun)</label>
        <input type="number" min={0} className="input w-32" value={data.trial_days}
          onChange={(e) => setData({ ...data, trial_days: Number(e.target.value) })} />
        <p className="text-xs text-gray-500 mt-1">Faqat bundan keyin ochiladigan do'konlarga ta'sir qiladi.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Karta raqami</label>
          <input className="input" value={data.card_number} placeholder="8600 0000 0000 0000"
            onChange={(e) => setData({ ...data, card_number: e.target.value })} />
        </div>
        <div>
          <label className="label">Karta egasi</label>
          <input className="input" value={data.card_holder}
            onChange={(e) => setData({ ...data, card_holder: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="label">To'lov bo'yicha izoh (tadbirkorga ko'rinadi)</label>
        <textarea rows={3} className="input" value={data.payment_note}
          onChange={(e) => setData({ ...data, payment_note: e.target.value })}
          placeholder="Masalan: izohga do'kon nomini yozing" />
      </div>
      <div className="flex items-center gap-4">
        <button type="submit" disabled={saving} className="btn btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? 'Saqlanmoqda...' : 'Saqlash'}
        </button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </form>
  );
};
